import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  defaultSupplyAppsListFilter,
  isSupplyAppsSortProperty,
  SupplyAppsSortDirection,
  SupplyInventoryAppStatus,
  type SupplyAppsListFilter,
} from './model';

export interface SupplyAppsRouteSearch {
  search?: string;
  statuses?: string;
  publishers?: string;
  platforms?: string;
  iabCategories?: string;
  page?: number;
  size?: number;
  sortProperty?: string | null;
  sortDirection?: string;
}

export function supplyAppsRouteSearchSchema(
  search: Record<string, unknown>,
): SupplyAppsRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const statuses = stringValue(search['statuses']);
  const publishers = stringValue(search['publishers']);
  const platforms = stringValue(search['platforms']);
  const iabCategories = stringValue(search['iabCategories']);
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  const sortProperty = optionalTrimmedString(search['sortProperty']);
  const sortDirection = optionalTrimmedString(search['sortDirection']);
  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(statuses !== undefined ? { statuses } : {}),
    ...(publishers !== undefined ? { publishers } : {}),
    ...(platforms !== undefined ? { platforms } : {}),
    ...(iabCategories !== undefined ? { iabCategories } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

export function parseSupplyAppsRouteSearch(
  search: SupplyAppsRouteSearch,
  publisherScoped = false,
): SupplyAppsListFilter {
  const defaultStatuses = publisherScoped ? [] : defaultSupplyAppsListFilter.statuses;
  return {
    searchTerm: search.search ?? defaultSupplyAppsListFilter.searchTerm,
    statuses:
      search.statuses === undefined
        ? defaultStatuses
        : commaValues(search.statuses).filter((status): status is SupplyInventoryAppStatus =>
            new Set<string>(Object.values(SupplyInventoryAppStatus)).has(status),
          ),
    publisherIds: commaValues(search.publishers),
    platformIds: commaValues(search.platforms),
    iabCategoryIds: commaValues(search.iabCategories),
    page: search.page ? search.page - 1 : defaultSupplyAppsListFilter.page,
    size: search.size ?? defaultSupplyAppsListFilter.size,
    sortProperty:
      search.sortProperty === null ||
      (search.sortProperty && isSupplyAppsSortProperty(search.sortProperty))
        ? search.sortProperty
        : defaultSupplyAppsListFilter.sortProperty,
    sortDirection:
      search.sortDirection === SupplyAppsSortDirection.Ascending ||
      search.sortDirection === SupplyAppsSortDirection.Descending
        ? search.sortDirection
        : defaultSupplyAppsListFilter.sortDirection,
  };
}

export function serializeSupplyAppsRouteSearch(
  filter: SupplyAppsListFilter,
  publisherScoped = false,
): SupplyAppsRouteSearch {
  const defaultStatuses = publisherScoped ? [] : defaultSupplyAppsListFilter.statuses;
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(!equivalentSets(filter.statuses, defaultStatuses)
      ? { statuses: filter.statuses.join(',') }
      : {}),
    ...(filter.publisherIds.length ? { publishers: filter.publisherIds.join(',') } : {}),
    ...(filter.platformIds.length ? { platforms: filter.platformIds.join(',') } : {}),
    ...(filter.iabCategoryIds.length ? { iabCategories: filter.iabCategoryIds.join(',') } : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultSupplyAppsListFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultSupplyAppsListFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultSupplyAppsListFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}

export function countActiveSupplyAppsSecondaryFilters(filter: SupplyAppsListFilter) {
  return [filter.publisherIds, filter.platformIds, filter.iabCategoryIds].filter(
    (values) => values.length > 0,
  ).length;
}

function stringValue(value: unknown) {
  return typeof value === 'string' ? value : undefined;
}

function commaValues(value: string | undefined) {
  return value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
}

function equivalentSets<T>(left: readonly T[], right: readonly T[]) {
  const leftSet = new Set(left);
  const rightSet = new Set(right);
  return leftSet.size === rightSet.size && [...leftSet].every((value) => rightSet.has(value));
}
